<template>
  <DrawerModal
    :is-open="isOpen"
    title="profile.my.general.title"
    :has-changes="hasChanges"
    :is-loading="isLoading"
    :is-disabled="!hasChanges || isInvalid"
    @close="handleClose"
    @submit="saveProfile"
  >
    <div :class="['profile-general', { 'is-mobile': isMobile }]">
      <div v-if="userForm" class="general-sections">
        <div class="message" v-if="errorMessage">
          <GamButtonMessage class="error-message" v-bind="errorMessage" />
        </div>

        <div class="section-card">
          <div class="form-row">
            <div class="input-with-support">
              <gam-input v-bind="username" v-model="userForm.username" @update="checkUsername" />
              <gam-support-text v-if="!usernameField.error" label="Letters, numbers, underscores, or dots." />
            </div>
            <gam-input v-bind="emoji" v-model="userForm.emoji" class="emoji" />
          </div>

          <div class="alt-support-text">Letters, numbers, underscores, or dots.</div>
        </div>

        <div class="section-card">
          <div class="section-title">
            {{ localize('profile.my.general.section.account.title') }}
          </div>

          <div class="form-row">
            <gam-input v-bind="firstName" v-model="userForm.firstName" />
            <gam-input v-bind="lastName" v-model="userForm.lastName" />
          </div>

          <div class="form-row">
            <gam-input v-bind="userMail" v-model="userForm.email" />
          </div>
        </div>

        <div class="section-card">
          <div class="section-title">
            {{ localize('profile.my.general.section.additional.title') }}
          </div>

          <div class="form-row">
            <gam-input-date v-bind="birthDate" v-model="userForm.birthDate" />
          </div>

          <div class="form-row">
            <gam-textarea
              v-model="userForm.aboutMe"
              placeholder="player.details.detail.personal.aboutMe"
              :value="userForm.aboutMe"
            />
          </div>
        </div>

        <div class="section-card">
          <div class="section-title">
            {{ localize('profile.my.general.section.type.title') }}
          </div>

          <PlayerRolesToggles
            :streamer-role="switchStreamerValue"
            @update:streamer="handleUpdateStreamer"
            :coach-role="switchCoachValue"
            @update:coach="handleUpdateCoach"
          />
        </div>
        <div class="section-card delete-section">
          <div class="section-title">
            {{ localize('profile.my.general.section.personal.title') }}
          </div>

          <GamButton v-bind="getDeleteAccountItem()" />
        </div>
      </div>
    </div>

    <GamConfirmModal
      v-if="showDeleteModal"
      :title="localize('dialog.user.delete.title')"
      :keep-label="localize('dialog.user.delete.dismiss')"
      :destructive-label="localize('dialog.user.delete.confirm')"
      :destructive-disabled="deleteUserLoading"
      @close="showDeleteModal = false"
      @keep="showDeleteModal = false"
      @confirm="handleDeleteAccount"
    >
      <template v-if="deleteUserError" #error>
        <div class="delete-account-error">{{ deleteUserError }}</div>
      </template>
    </GamConfirmModal>
  </DrawerModal>
</template>

<script setup lang="ts">
import { PlayerType } from '@/core/data/user/user.type';
import { gambits, localize } from '@/core/gambits';
import FormHelper from '@/core/helpers/form.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useFormStore } from '@/stores/form.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamInput from '@/views/components/GamInput.vue';
import GamInputDate from '@/views/components/GamInputDate.vue';
import GamSupportText from '@/views/components/GamSupportText.vue';
import GamTextarea from '@/views/components/GamTextarea.vue';
import {
  GamButtonMessageVariant,
  GamButtonSize,
  GamButtonVariant,
} from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import type { GamButtonMessageType, GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import type { ProfileUserForm, ProfileUserValidator } from '@/views/composables/models/form.interface';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PlayerRolesToggles from './components/PlayerRolesToggles.vue';

interface GeneralSectionProps {
  isOpen: boolean;
  onUpdate?: () => void;
}

const props = defineProps<GeneralSectionProps>();
const emit = defineEmits<{ close: []; update: [] }>();

const resetToServerState = () => {
  const details = userDetails.value;
  if (!details) return;
  userForm.firstName = details.firstName || '';
  userForm.lastName = details.lastName || '';
  userForm.username = details.username || '';
  userForm.email = user.value?.email || '';
  userForm.emoji = details.emoji || '';
  userForm.aboutMe = details.aboutMe || '';
  userForm.profileImage = details.profileImageBlobHash || null;
  userForm.chessboardImage = details.chessBoardImageBlobHash || null;
  userForm.playerTypes = details.playerTypes || [];
  userForm.birthDate = details.birthDate ? new Date(details.birthDate) : undefined;
  Object.assign(form, { ...userForm });
  switchCoachValue.value = !!details.playerTypes?.includes(PlayerType.COACH);
  switchStreamerValue.value = !!details.playerTypes?.includes(PlayerType.STREAMER);
  errorMessage.value = null;
  formStore.setUsernameValidated(true);
  formStore.setUsernameError(null);
};

const handleClose = () => {
  resetToServerState();
  emit('close');
};

const formId = FormId.GENERAL_PROFILE;
const isLoading = ref<boolean>(false);

const emojiValidator = FormHelper.emojiValidator;
// A handle from before the username rules stays valid until its owner changes it.
const usernameValidator = (username: string): boolean =>
  username === userDetails.value?.username || FormHelper.usernameValidator(username);
const formStore = useFormStore<ProfileUserForm, ProfileUserValidator>(formId)();
const { usernameField } = storeToRefs(formStore);

const authStore = useAuthStore();
const { userDetails, user } = storeToRefs(authStore);

const router = useRouter();

const switchCoachValue = ref<boolean>(!!userDetails.value?.playerTypes?.includes(PlayerType.COACH));
const switchStreamerValue = ref<boolean>(!!userDetails.value?.playerTypes?.includes(PlayerType.STREAMER));

const showDeleteModal = ref(false);
const deleteUserLoading = ref<boolean>(false);
const deleteUserError = ref<string | null>(null);

const errorMessage = ref<GamButtonMessageType | null>(null);

const getForm = reactive({
  firstName: userDetails.value?.firstName || '',
  lastName: userDetails.value?.lastName || '',
  username: userDetails.value?.username || '',
  email: user.value?.email || '',
  emoji: userDetails.value?.emoji || '',
  aboutMe: userDetails.value?.aboutMe || '',
  profileImage: userDetails.value?.profileImageBlobHash || null,
  chessboardImage: userDetails.value?.chessBoardImageBlobHash || null,
  playerTypes: userDetails.value?.playerTypes || [],
  birthDate: userDetails.value?.birthDate ? new Date(userDetails.value?.birthDate) : undefined,
});

const form = reactive({
  firstName: userDetails.value?.firstName || '',
  lastName: userDetails.value?.lastName || '',
  username: userDetails.value?.username || '',
  email: user.value?.email || '',
  emoji: userDetails.value?.emoji || '',
  aboutMe: userDetails.value?.aboutMe || '',
  profileImage: userDetails.value?.profileImageBlobHash || null,
  chessboardImage: userDetails.value?.chessBoardImageBlobHash || null,
  playerTypes: userDetails.value?.playerTypes || [],
  birthDate: userDetails.value?.birthDate ? new Date(userDetails.value?.birthDate) : undefined,
});
const userForm = reactive({ ...form });

const dateValidator = (date: Date): boolean => {
  return FormHelper.isValidDate(date, gambits.configService.getAuthMinAge());
};

const getRules = {
  firstName: { required },
  lastName: { required },
  email: { required, email },
  username: { required, usernameValidator },
  emoji: { emojiValidator },
  birthDate: { required, dateValidator },
};

onMounted(async () => {
  formStore.setForm({ form: getForm, rules: getRules });
  formStore.setUsernameValidated(true);
});

const v$ = useVuelidate(getRules, userForm);

const isInvalid = computed((): boolean => {
  return v$.value.$invalid || !usernameField.value.isValidated;
});

const checkUsername = debounce(() => {
  if (userForm.username === userDetails.value?.username) {
    formStore.setUsernameValidated(true);
    formStore.setUsernameError(null);

    return;
  }

  formStore.setUsernameValidated(null);
  FormHelper.checkUsername(formId, userForm.username || '', v$);

  nextTick(() => {
    if (userDetails.value?.username === userForm.username) {
      formStore.setUsernameValidated(true);
      formStore.setUsernameError(null);
    } else {
      formStore.setUsernameValidated(null);
      FormHelper.checkUsername(formId, userForm.username || '', v$);
    }
  });
}, 400);

const getDeleteAccountItem = (): GamButtonType => {
  return {
    variant: GamButtonVariant.AFFORDED,
    size: GamButtonSize.SMALL,
    label: 'profile.my.general.section.personal.delete.button',
    onClick: () => {
      showDeleteModal.value = true;
    },
  };
};

onBeforeMount(() => {
  // @ts-ignore
  window.deleteAction = async function () {
    showDeleteModal.value = true;
  };
});

const handleDeleteAccount = async () => {
  deleteUserLoading.value = true;
  deleteUserError.value = null;
  const deleteResult = await gambits.authService.delete();

  if (deleteResult.data) {
    await gambits.authService.logout();
    nextTick(() => {
      router.push('/');
    });
  }
  if (deleteResult.error && deleteResult.error.code === 400 && deleteResult.error.message) {
    deleteUserError.value = deleteResult.error.message;
  }
  deleteUserLoading.value = false;
};

const handleUpdateStreamer = (value: boolean) => {
  switchStreamerValue.value = value;
};

const handleUpdateCoach = (value: boolean) => {
  switchCoachValue.value = value;
};

const firstName = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_NAME,
    value: userForm.firstName,
    placeholder: 'auth.page.registration.form.firstName.placeholder',
  };
});

const lastName = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_SURNAME,
    value: userForm.lastName,
    placeholder: 'auth.page.registration.form.lastName.placeholder',
  };
});

const userMail = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_EMAIL,
    value: userForm.email,
    placeholder: 'auth.page.registration.form.email.placeholder',
    isDisabled: true,
  };
});

const birthDate = computed(() => {
  return {
    name: GamInputName.GAM_DATETIME,
    value: userForm.birthDate,
  };
});

const username = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_USERNAME,
    value: userForm.username,
    placeholder: 'auth.page.registration.form.username.placeholder',
    isLoading: usernameField.value.isLoading,
    isError: usernameField.value.isValidated === false,
    message: usernameField.value.error,
    rightIcon: usernameField.value.isValidated ? GamIconName.CHECK : undefined,
    maxLength: 30,
    isPrimary: true,
    isLowerCase: true,
    isUsername: true,
  };
});

const emoji = computed((): GamInputType => {
  return {
    name: GamInputName.EMOJI,
    value: userForm.emoji,
    placeholder: 'auth.page.registration.form.emoji.placeholder',
    isEmoji: true,
  };
});

watch(() => userDetails.value, resetToServerState);

const saveProfile = async () => {
  isLoading.value = true;
  const result = await gambits.userService.updateUser(userForm);

  await authStore.fetchUserInfo(true);
  isLoading.value = false;

  if (result.data) {
    emit('update');
    props.onUpdate?.();
    emit('close');
  } else {
    const label = result.error?.message || 'Something went wrong.';
    errorMessage.value = {
      label,
      variant: GamButtonMessageVariant.ERROR,
    };
  }
};

watch(switchCoachValue, (value: boolean) => {
  if (userForm.playerTypes) {
    userForm.playerTypes = FormHelper.syncPlayerType(PlayerType.COACH, userForm.playerTypes, !value);
  }
});

watch(switchStreamerValue, (value: boolean) => {
  if (userForm.playerTypes) {
    userForm.playerTypes = FormHelper.syncPlayerType(PlayerType.STREAMER, userForm.playerTypes, !value);
  }
});

const hasChanges = computed(() => {
  return JSON.stringify(userForm) !== JSON.stringify(form);
});
</script>

<style scoped lang="scss">
.input-with-support {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  width: 15rem;
  min-height: 4rem;
  margin-bottom: var(--spacing-l);

  .gam-support-text {
    position: absolute;
    top: 3.75rem;
    padding-left: 23px;
    width: max-content;
  }
}

.is-mobile :deep(.gam-support-text) {
  position: static;
  display: none;
}

.alt-support-text {
  display: none;
}

.is-mobile .alt-support-text {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--color-white-30);
  line-height: 110%;
  letter-spacing: 0.05em;
  margin-top: -2.5rem;
  padding: 0 2.5rem 0 1.5rem;
}

.input-with-support {
  padding-bottom: 1rem;
}

.profile-general {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);

  .section-title {
    font-size: 16px;
    font-weight: 700;
    flex-grow: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-bottom: var(--spacing-md);
  }

  .general-sections {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-l);
    align-self: stretch;

    .form-row {
      width: 100%;
      display: flex;
      gap: var(--spacing-md);

      .emoji {
        width: 80px;
      }
    }
  }
}

.section-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--spacing-l);
  gap: var(--spacing-md);
  border-radius: var(--radius-small);
  border: 1px solid var(--color-white-10);
}

.delete-section {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  .section-title {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.message {
  display: flex;
  width: 100%;
  justify-content: center;
  padding-bottom: 1rem;
}

.delete-account-error {
  font-size: 13px;
  color: #e74c3c;
  margin-bottom: var(--spacing-md);
}
</style>
